import React, { useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { theme, Dropdown } from 'antd';
import { RightOutlined, MoreOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { StationInsight } from '@/types/insights.types';
import { useStores } from '@/hooks/use-stores';
import {
  getFormattedSfsContract,
  getCareProgramIcon,
  shouldDisplayCareStatus,
} from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/PackageCareIndication/PackageCareIndication.utils';
import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';
import * as S from './AssistantInsightCard.styles';

const { useToken } = theme;

export interface ActionButton {
  label: string;
  onClick: () => void;
}

export interface AdditionalAction {
  key: string;
  label: string;
  onClick: () => void;
}

interface AssistantInsightCardProps {
  insight: StationInsight;
  onClick?: () => void;
  onAddClick?: () => void;
  actionButtons?: ActionButton[];
  additionalActionButtons?: AdditionalAction[];
  showArrowIcon?: boolean;
  fixedHeight?: boolean;
  disabled?: boolean;
}

const AssistantInsightCard: React.FC<AssistantInsightCardProps> = observer(
  ({
    insight,
    onClick,
    onAddClick,
    actionButtons,
    additionalActionButtons,
    showArrowIcon = true,
    fixedHeight = true,
    disabled = false,
  }) => {
    const { token } = useToken();
    const { t } = useTranslation();
    const { devicesStore, stationStore } = useStores();
    const [isHovered, setIsHovered] = useState(false);

    const getGradientBackground = () => {
      switch (insight.type) {
        case 'risk':
          return 'linear-gradient(210.72deg, rgba(255, 91, 85, 0) 50.92%, rgba(255, 91, 85, 0.1) 104.04%), linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.04) 100%)';
        case 'maintenance':
          return 'linear-gradient(210.72deg, rgba(255, 131, 25, 0) 50.92%, rgba(255, 131, 25, 0.1) 104.04%), linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.04) 100%)';
        case 'energy':
          return 'linear-gradient(210.72deg, rgba(82, 196, 26, 0) 50.92%, rgba(82, 196, 26, 0.1) 104.04%), linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.04) 100%)';
        default:
          return 'linear-gradient(210.72deg, rgba(255, 91, 85, 0) 50.92%, rgba(255, 91, 85, 0.1) 104.04%), linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.04) 100%)';
      }
    };

    const getTypeColor = () => {
      switch (insight.type) {
        case 'risk':
          return token.colorError;
        case 'maintenance':
          return token.colorWarning;
        case 'energy':
          return '#46af16';
        default:
          return token.colorError;
      }
    };

    const getTypeLabel = () => {
      switch (insight.type) {
        case 'risk':
          return 'Risk';
        case 'maintenance':
          return 'Maintenance';
        case 'energy':
          return 'Energy';
        default:
          return 'Risk';
      }
    };

    // Get device metadata and asset for care icon logic
    // Access observable arrays directly so MobX can track changes
    const deviceMetadata = useMemo(() => {
      if (!insight.deviceId || !devicesStore) return null;
      return devicesStore.getDeviceById(insight.deviceId);
    }, [insight.deviceId, devicesStore, devicesStore?.devicesMetadata]);

    const currentAsset = useMemo(() => {
      if (!insight.deviceId || !stationStore?.assetsDevices) return null;
      return stationStore.assetsDevices.find(a => a.deviceId === insight.deviceId) || null;
    }, [insight.deviceId, stationStore?.assetsDevices]);

    // Use servicePackageIndicator from insight if available, otherwise get from asset
    const servicePackageIndicator = useMemo(() => {
      if (insight.servicePackageIndicator) {
        return insight.servicePackageIndicator;
      }
      return currentAsset?.SfsContract;
    }, [insight.servicePackageIndicator, currentAsset?.SfsContract]);

    // Construct Entity-like object for shouldDisplayCareStatus
    const entity = useMemo((): Entity | null => {
      if (!deviceMetadata) return null;
      return {
        deviceId: deviceMetadata.id,
        deviceType: deviceMetadata.deviceTypes?.[0] || (deviceMetadata.deviceType as any),
        modelDisplayName: deviceMetadata.modelDisplayName || deviceMetadata.name,
        name: deviceMetadata.name,
        brand: deviceMetadata.brand || '',
        controllerBrand: deviceMetadata.controllerBrand || '',
        controllerId: deviceMetadata.controllerId || '',
        controllerModel: deviceMetadata.controllerModel || '',
        deviceControllerName: deviceMetadata.deviceControllerName || '',
        deviceKind: deviceMetadata.deviceKind as any,
        elementIndex: 0,
        isIndependent: false,
        primaryPressureSensor: '',
        primaryFlowSensor: '',
        onDevices: [],
      };
    }, [deviceMetadata]);

    const formattedSfsContract = useMemo(() => {
      if (!servicePackageIndicator) return null;
      return getFormattedSfsContract(servicePackageIndicator);
    }, [servicePackageIndicator]);

    // Use the same logic as asset drawer to determine if care should be displayed
    const shouldShowCareIcon = useMemo(() => {
      // Only show for devices (not station-level insights)
      if (!insight.deviceId || !entity || !currentAsset) return false;

      // Use the same logic as PackageCareIndication component
      const displayCareStatus = shouldDisplayCareStatus(currentAsset, entity, t);
      if (!displayCareStatus) return false;

      // Check if care program exists and is valid
      if (!formattedSfsContract) return false;
      const isNotCareAble = formattedSfsContract.includes('notcareable');
      const isNoCareProgram = formattedSfsContract.includes('nocareprogram');
      return !isNotCareAble && !isNoCareProgram;
    }, [insight.deviceId, entity, currentAsset, formattedSfsContract, t]);

    // Get the appropriate care program icon
    const CareProgramIcon = useMemo(() => {
      if (!formattedSfsContract || !shouldShowCareIcon) return null;
      return getCareProgramIcon(formattedSfsContract);
    }, [formattedSfsContract, shouldShowCareIcon]);

    const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
      domEvent?.stopPropagation();
      const action = additionalActionButtons?.find(a => a.key === key);
      if (action) {
        action.onClick();
      }
    };

    const menuItems: MenuProps['items'] =
      additionalActionButtons?.map(action => ({
        key: action.key,
        label: action.label,
      })) || [];

    const hasActionButtons = actionButtons && actionButtons.length > 0;
    const hasAdditionalActions = additionalActionButtons && additionalActionButtons.length > 0;
    const showActionBar = isHovered && (hasActionButtons || hasAdditionalActions);
    const showThreeDots = hasAdditionalActions && additionalActionButtons.length > 0;

    return (
      <S.CardContainer
        $gradient={getGradientBackground()}
        $fixedHeight={fixedHeight}
        $disabled={disabled}
        onClick={disabled ? undefined : onClick}
        onMouseEnter={() => !disabled && setIsHovered(true)}
        onMouseLeave={() => !disabled && setIsHovered(false)}
      >
        <S.CardContentWrapper>
          <S.CardHeader>
            <S.ValueChip>
              <S.ValueText>{insight.value || 'N/A'}</S.ValueText>
            </S.ValueChip>
            {showArrowIcon && (
              <S.ArrowIconButton
                onClick={e => {
                  e.stopPropagation();
                  if (!disabled) {
                    onAddClick?.();
                  }
                }}
                disabled={disabled}
              >
                <RightOutlined />
              </S.ArrowIconButton>
            )}
          </S.CardHeader>

          <S.CardContent>
            <S.CardTitle>{insight.name}</S.CardTitle>
            <S.CardMeta>
              <S.DeviceInfo>{insight.device}</S.DeviceInfo>
              {insight.date && (
                <>
                  <S.Dot />
                  <S.DateInfo>{insight.date}</S.DateInfo>
                </>
              )}
              {shouldShowCareIcon && CareProgramIcon && (
                <>
                  <S.Dot />
                  <S.CareIconWrapper>
                    <CareProgramIcon width={48} height={20} />
                  </S.CareIconWrapper>
                </>
              )}
            </S.CardMeta>
            <S.CardDescription>{insight.description}</S.CardDescription>
          </S.CardContent>
        </S.CardContentWrapper>

        <S.CardFooter>
          <S.TypeIndicator $color={getTypeColor()} />
          <S.FooterContent>
            <S.TypeLabel $color={getTypeColor()}>{getTypeLabel()}</S.TypeLabel>
            {showActionBar && (
              <S.ActionButtonsContainer>
                {hasActionButtons &&
                  actionButtons.map((button, index) => (
                    <S.ActionButton
                      key={index}
                      onClick={e => {
                        e.stopPropagation();
                        if (!disabled) {
                          button.onClick();
                        }
                      }}
                      disabled={disabled}
                    >
                      {button.label}
                    </S.ActionButton>
                  ))}
                {showThreeDots && (
                  <div onClick={e => e.stopPropagation()}>
                    <Dropdown
                      menu={{ items: menuItems, onClick: handleMenuClick }}
                      trigger={['click']}
                      getPopupContainer={trigger => trigger.parentElement || document.body}
                    >
                      <S.ThreeDotsButton
                        onClick={e => {
                          e.stopPropagation();
                        }}
                      >
                        <MoreOutlined style={{ fontSize: 16, color: token.colorPrimary }} />
                      </S.ThreeDotsButton>
                    </Dropdown>
                  </div>
                )}
              </S.ActionButtonsContainer>
            )}
          </S.FooterContent>
        </S.CardFooter>
      </S.CardContainer>
    );
  },
);

AssistantInsightCard.displayName = 'AssistantInsightCard';

export default AssistantInsightCard;
